import { useEffect, useState, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useConfirm } from '../components/ConfirmProvider';
import { LoadingIndicator } from '../components/LoadingIndicator';
import {
  getSite,
  listActiveSignIns,
  bulkSignOut,
  type Site,
  type SignIn,
} from '../lib/db';

function nowAsLocalDateTimeInput(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export function CloseSite() {
  const { siteId } = useParams<{ siteId: string }>();
  const nav = useNavigate();
  const { alert } = useConfirm();

  const [site, setSite] = useState<Site | null>(null);
  const [active, setActive] = useState<SignIn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [closeTime, setCloseTime] = useState<string>(nowAsLocalDateTimeInput());

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    Promise.all([getSite(siteId), listActiveSignIns(siteId)])
      .then(([s, a]) => { setSite(s); setActive(a); })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [siteId]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!siteId || !site) return;
    setSubmitting(true);
    setError(null);
    try {
      const iso = new Date(closeTime).toISOString();
      const { signedOut } = await bulkSignOut(siteId, iso);
      // Briefly let the user know via the success banner pattern, then go back
      await alert({
        title: signedOut === 0 ? 'Already closed' : 'Site closed',
        body: signedOut === 0
          ? 'Nobody to sign out — site is already closed.'
          : `Signed out ${signedOut} ${signedOut === 1 ? 'person' : 'people'} at ${fmtTime(iso)}.`,
      });
      nav(`/sites/${siteId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  }

  if (loading) return <AppShell title="Loading…" back={`/sites/${siteId ?? ''}`}><LoadingIndicator /></AppShell>;
  if (!site) return <AppShell title="Close site" back={`/sites/${siteId ?? ''}`}><div className="banner banner-error">{error ?? 'Site not found.'}</div></AppShell>;

  return (
    <AppShell title="Close site" back={`/sites/${site.id}`}>
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0 }}><strong>{site.name}</strong></p>
        <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }}>
          Sign every active person out at the same time and lock down for the day.
        </p>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {active.length === 0 ? (
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            Nobody is currently active on this site — nothing to close.
          </p>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ marginTop: 12 }}
            onClick={() => nav(`/sites/${site.id}`)}
          >
            Back
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit}>
          <h3 className="section-title">Still on site ({active.length})</h3>
          <div className="card" style={{ marginBottom: 16 }}>
            <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: 14 }}>
              {active.map((p) => (
                <li key={p.id} style={{ marginBottom: 4 }}>
                  <strong>{p.full_name}</strong>
                  <span className="muted"> · {p.company} · in at {fmtTime(p.time_in)}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="field needs-input">
            <label htmlFor="close-time">Sign everyone out at *</label>
            <input
              id="close-time"
              type="datetime-local"
              value={closeTime}
              onChange={(e) => setCloseTime(e.target.value)}
              required
            />
            <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
              Defaults to right now. Adjust if the site actually closed earlier.
            </p>
          </div>

          <div className="card" style={{ background: 'var(--brand-pale)', borderColor: 'var(--brand-light)', marginBottom: 16 }}>
            <p style={{ margin: 0, fontSize: 13 }}>
              <strong>About to:</strong> sign out{' '}
              <strong>{active.length} {active.length === 1 ? 'person' : 'people'}</strong> at{' '}
              <strong>{closeTime ? fmtTime(new Date(closeTime).toISOString()) : '—'}</strong>.
              They won't be on tomorrow's active list.
            </p>
          </div>

          <div className="btn-stack">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Closing site…' : 'Sign everyone out'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => nav(`/sites/${site.id}`)} disabled={submitting}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </AppShell>
  );
}
